/**
 * Central function to produce download filenames for documents.
 *
 * Every download site must go through this function so that filenames
 * are consistent, sanitized, and never the generic "PDF du document".
 */

const UNSAFE_CHARS = /[/\\:*?"<>|]/g
const MAX_LENGTH = 100

function sanitize(name: string): string {
  return name
    .replace(UNSAFE_CHARS, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_LENGTH)
}

/**
 * Build a PDF filename from a document record.
 *
 * @param record  The document object (invoice, quote, order, etc.)
 * @param draftLabel  Translated "Draft" label (lang.labels.draft)
 * @param typeLabel   Translated type label (e.g. lang.labels.invoice)
 * @returns  Sanitized filename WITH .pdf extension
 */
export function documentPdfFilename(record: any, draftLabel: string, typeLabel: string): string {
  const number =
    record?.invoice_number ??
    record?.quote_number ??
    record?.order_number ??
    record?.provider_invoice_number ??
    record?.provider_order_number ??
    record?.sale_number ??
    record?.delivery_note_number ??
    record?.provider_quote_number ??
    null

  if (number && typeof number === 'string' && number.length > 0) {
    return sanitize(number) + '.pdf'
  }

  return sanitize(`${draftLabel}-${typeLabel}`) + '.pdf'
}

/**
 * Build a filename for an attachment / voucher.
 * Attachments keep their original name; this only adds a .pdf fallback and sanitizes.
 */
export function attachmentPdfFilename(name: string | undefined | null, fallback: string = 'document'): string {
  const base = name && name.length > 0 ? name : fallback
  const sanitized = sanitize(base)
  return sanitized.endsWith('.pdf') ? sanitized : sanitized + '.pdf'
}

/**
 * Build a fallback filename for accounting exports (balance sheet, income statement, etc.)
 * Used when the server does not provide a Content-Disposition filename.
 */
export function exportFallbackFilename(
  typeLabel: string,
  companyName: string,
  period: string,
  format: string
): string {
  const base = [typeLabel, companyName, period]
    .filter(Boolean)
    .join('_')
    .replace(/\s+/g, '_')
  return sanitize(base) + '.' + format
}

export { sanitize as sanitizeFilename }
